import { useAtomCallback } from "jotai/utils";
import { useCallback, useMemo } from "react";

import { $mutationExecutor } from "../../internal/request-page/mutation-executor.atom";
import {
	type MutationBuilder,
	type MutationExecutorInput,
	createMutationBuilder,
} from "../../internal/request-page/mutation-handle";

/**
 * Hook that provides a MutationBuilder for executing mutations from code cells.
 *
 * The MutationBuilder returns MutationHandle objects with a mutateAsync() method
 * and onSettled callback support.
 *
 * @param documentId - The document ID to scope mutations to
 * @returns MutationBuilder function, or undefined if documentId is not provided
 *
 * @example
 * ```ts
 * const mutation = useMutationBuilder(documentId);
 * if (mutation) {
 *   const handle = mutation({
 *     request: "insertUser",
 *     onSettled: () => usersQuery.refetch(),
 *   });
 *   await handle.mutateAsync({ name: "Alice" });
 * }
 * ```
 */
export const useMutationBuilder = (documentId: string | undefined): MutationBuilder | undefined => {
	const dispatch = useAtomCallback(
		useCallback(
			(_get, set, input: MutationExecutorInput) => {
				if (!documentId) {
					throw new Error("documentId is required for mutation execution");
				}
				return set($mutationExecutor(documentId), input);
			},
			[documentId]
		)
	);

	return useMemo(() => {
		if (!documentId) {
			return undefined;
		}
		return createMutationBuilder(dispatch);
	}, [documentId, dispatch]);
};
